import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import IconList from "@kikoff/components/src/v1/info/IconList";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { web } from "@kikoff/proto/src/protos";
import { partition } from "@kikoff/utils/src/array";
import { format } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import ViewTemplate from "@component/dashboard/offer/view_template";
import PersistentAppBar from "@component/flows/components/PersistentAppBar";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import Line from "@component/line";
import ColumnList from "@component/lists/grid_list";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import Tabs from "@component/navigation/tabs";
import ProductBreakdown from "@component/shopping/product_breakdown";
import ProductEntry from "@component/shopping/product_entry";
import { selectFeatureFlag } from "@feature/page";
import {
  fetchCheckoutPreview,
  setCheckoutPreview,
  updateBag,
  updateCatalog,
} from "@feature/shopping";
import {
  getKikoffCreditAccountMentalModelName,
  getKikoffCreditServiceGenericBothMentalModelName,
} from "@src/constants";
import { useOverlaysController } from "@src/overlay";
import { track } from "@util/analytics";

import styles from "./select_product.module.scss";

const featured = {
  label: "Featured ✨",
  filter: (product) => product.type === web.public_.Product.Type.SUBSCRIPTION,
  singleProductDetails: ({ preview, product }) => {
    const pricePerMonth = format.money.cents(
      preview?.creditLineAgreement.minimumPaymentAmountCents
    );

    const items = [
      "Ability to build payment history on auto-pilot with auto-renew",
      "Manage your credit building progress with score and report updates",
      `Just ${pricePerMonth} / month`,
      "Exclusive access to our Credit Builder Loan after completing your first payment!",
    ];

    return (
      <div className={styles["plus-details"]}>
        <header className="text:eyebrow-3 color:moderate">
          {product.title} benefits:
        </header>
        <IconList icon="👍" align="baseline" items={items} />
        {preview ? (
          <ProductBreakdown preview={preview} />
        ) : (
          <GenericSpinner center />
        )}
      </div>
    );
  },
};

const tabsMap: Record<
  string,
  {
    label: string;
    filter(product: web.public_.IProduct): any;
    // Only show first found product with details, this is a function instead of
    // a component to discourage using state
    singleProductDetails?(ctx: {
      preview: web.public_.ICheckoutPreviewResponse;
      product: web.public_.IProduct;
    }): React.ReactNode;
  }[]
> = {
  categories: [
    {
      label: "All",
      filter: () => true,
    },
    {
      label: "e-Books",
      filter: (product) =>
        product.type === web.public_.Product.Type.BOOK ||
        product.type === web.public_.Product.Type.BOOKS_BUNDLE,
    },
    {
      label: "e-Courses",
      filter: (product) => product.type === web.public_.Product.Type.COURSE,
    },
  ],
  featured: [
    featured,
    {
      label: "All products",
      filter: () => true,
    },
  ],
  mobileFeatured: [
    featured,
    {
      label: "Unavailable In-app",
      filter: (product) =>
        product.type !== web.public_.Product.Type.SUBSCRIPTION,
    },
  ],
};

interface SelectProductViewProps extends PageRegistration {}

export default function SelectProductView({
  configFlow,
  Button,
  page,
}: SelectProductViewProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();
  const overlays = useOverlaysController();

  const products = useSelector((state) => state.shopping.products);
  const creditLine = useSelector((state) => state.creditLine.account);
  const isWebview = useSelector((state) => state.page.isWebview);
  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const showStoreTabs = useSelector(selectFeatureFlag("show_store_tabs"));

  const isNewUser = useMemo(() => !creditLine, []);

  const showTabs = (showStoreTabs && !isWebview) || isNewUser;

  const [tabIndex, setTabIndex] = useState(0);

  const tabGroup = isNewUser
    ? isWebview
      ? "mobileFeatured"
      : "featured"
    : "categories";
  const tabs = tabsMap[tabGroup];
  const currentTab = tabs[tabIndex];
  const tabLists = useMemo(
    () => tabs.map((tab) => products.filter(tab.filter)),
    [products, tabs]
  );
  const product = tabLists[tabIndex][0];

  const pricePerMonth = format.money(
    preview?.creditLineAgreement.minimumPaymentAmountCents,
    { includeCents: true }
  );

  configFlow
    .layout(PersistentAppBar, { title: "Set up your credit plan" })
    .button(
      currentTab.singleProductDetails && product?.title && preview && (
        <Button>Continue with {product.title}</Button>
      ),
      [currentTab, product, preview]
    );

  useEffect(() => {
    dispatch(updateCatalog(true))
      .then((data) => {
        const creditLineIncreaseIncentive = data.shoppingIncentives.find(
          (incentive) => incentive.creditLineIncrease
        );
        if (!creditLineIncreaseIncentive) return;
        overlays.push("shopping/credit_line_increase", {
          creditLineIncreaseIncentive,
        });
      })
      .catch((err: Error) => {
        error.throw(err.message);
      });
  }, []);

  const note = isNewUser ? (
    `You can pick between our ${getKikoffCreditServiceGenericBothMentalModelName()} or an e-book, if you’re into reading. All items will help you build your credit by building payment history.`
  ) : (
    <>
      {creditLine
        ? "Choose another item to start extending your positive payment history."
        : `Make a purchase to open your ${getKikoffCreditAccountMentalModelName()}. Purchase amounts vary but will only cost you ${pricePerMonth}/month.`}
      <br />
      <br />
      Click an item to see details and proceed to checkout.
    </>
  );

  return (
    <PageAlign scroll overscroll={false}>
      <ViewTemplate className={styles["select-product-view"]} note={note}>
        <div className={styles.catalog}>
          {showTabs && (
            <Tabs
              variant="underline"
              activeIndex={tabIndex}
              items={tabs.map((tab, i) => ({
                label: tab.label,
                onClick() {
                  setTabIndex(i);
                },
              }))}
            />
          )}
          {!products?.length ? (
            <GenericSpinner center />
          ) : currentTab.singleProductDetails ? (
            <SingleProductView
              product={product}
              getDetails={currentTab.singleProductDetails}
            />
          ) : (
            <ProductList
              products={showTabs ? tabLists[tabIndex] : products}
              onContinue={() => {
                page.controller.next();
              }}
            />
          )}
        </div>
      </ViewTemplate>
    </PageAlign>
  );
}

interface SingleProductViewProps {
  product: web.public_.IProduct;
  getDetails: typeof tabsMap[string][number]["singleProductDetails"];
}

function SingleProductView({ product, getDetails }: SingleProductViewProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();

  const preview = useSelector((state) => state.shopping.checkoutPreview);

  useEffect(() => {
    dispatch(fetchCheckoutPreview(product.token)).catch((e) => {
      error.throw(e);
    });
  }, [getDetails]);

  useEffect(() => {
    if (preview)
      dispatch(
        updateBag({
          items: [{ product, quantity: 1 }],
          autoRenew: {
            status: preview.autoRenew?.status,
          },
        })
      );
  }, [preview]);

  return (
    <div className={styles["single-product-view"]}>
      <ProductEntry product={product} contentOnly />
      <Line />
      {getDetails({ preview, product })}
    </div>
  );
}

interface ProductListProps {
  products: web.public_.IProduct[];
  onContinue(product: web.public_.IProduct): void;
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  onContinue,
}) => {
  const [more, setMore] = useState(false);

  const productGroups: web.public_.IProduct[][] = useMemo(
    () => partition(products, (product) => !product.tags.includes("hidden")),
    [products]
  );

  const visibleProducts = more ? productGroups.flat() : productGroups[0];
  const [eligibleProducts, ineligibileProducts] = useMemo(
    () =>
      partition(
        visibleProducts,
        (product) => !product.tags.includes("!ineligible-platform")
      ),
    [visibleProducts]
  );

  return (
    <div className={styles["product-list"]}>
      <ColumnList
        columnWidth={300}
        propsFor={{ col: { className: styles.col } }}
      >
        {renderProducts(eligibleProducts, onContinue, false)}
      </ColumnList>
      {ineligibileProducts.length > 0 && (
        <>
          <div className={styles["app-store-ineligbility-info"]}>
            The following items are not available for purchase in the mobile
            app.
          </div>
          <ColumnList
            columnWidth={300}
            propsFor={{ col: { className: styles.col } }}
          >
            {renderProducts(ineligibileProducts, onContinue, true)}
          </ColumnList>
        </>
      )}

      {!!productGroups[1].length && !more && (
        <ContainerButton className={styles.more} onClick={() => setMore(true)}>
          See more
        </ContainerButton>
      )}
    </div>
  );
};

function renderProducts(products, onContinue, disabled) {
  return products.map((product) => (
    <Product
      key={product.token}
      product={product}
      onContinue={onContinue}
      disabled={disabled}
    />
  ));
}

interface ProductProps {
  product: web.public_.IProduct;
  onContinue(product: web.public_.IProduct): void;
  disabled?: boolean;
}

const Product: React.FC<ProductProps> = ({ product, onContinue, disabled }) => {
  const dispatch = useDispatch();
  const error = useErrorConsumer();
  const overlays = useOverlaysController();

  function onClick() {
    track.tap("Store - Select Item", {
      "item name": product.title,
      "product token": product.token,
    });

    dispatch(setCheckoutPreview(null));
    dispatch(fetchCheckoutPreview(product.token)).catch((e) => {
      error.throw(e.message);
    });
    overlays
      .push("shopping/product", { productToken: product.token })
      .then((returnValue) => {
        if (returnValue === "selected") onContinue(product);
      });
  }

  const productComponent = (
    <ProductEntry product={product} onClick={onClick} disabled={disabled} />
  );

  if (product.tags.includes("recommended"))
    return (
      <div className={styles["product-highlight-container"]}>
        <div className={styles["highlight-title"]}>MOST POPULAR</div>
        {productComponent}
      </div>
    );

  return productComponent;
};
